import {
  Component,
  DestroyRef,
  inject,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { MenuItem } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { PopoverModule } from "primeng/popover";
import { ScrollerModule } from "primeng/scroller";
import { filter, finalize } from "rxjs";
import { AlertStatus } from "../../interfaces/alert-type.enum";
import { Alert, AlertsSummary } from "../../interfaces/alert.interface";
import { Pagination } from "../../interfaces/pagination.interface";
import { AlertService } from "../../services/alert.service";
import { UserService } from "../../services/user.service";
import { UtilService } from "../../services/util.service";
import { AlertComponent } from "../alert/alert.component";
import { ProjectSearchComponent } from "../project-search/project-search.component";

@Component({
  selector: "app-breadcrumb",
  imports: [
    BreadcrumbModule,
    ButtonModule,
    PopoverModule,
    AlertComponent,
    TranslocoDirective,
    ScrollerModule,
    ProjectSearchComponent,
  ],
  templateUrl: "./breadcrumb.component.html",
  styleUrl: "./breadcrumb.component.scss",
})
export class BreadcrumbComponent {
  protected router = inject(Router);
  protected activatedRoute = inject(ActivatedRoute);
  protected tLoco = inject(TranslocoService);
  protected destroyRef = inject(DestroyRef);
  protected utilService = inject(UtilService);
  protected alertService = inject(AlertService);
  protected userService = inject(UserService);

  breadcrumbs: WritableSignal<MenuItem[]> = signal([]);
  alerts: WritableSignal<Alert[]> = signal([]);
  pagination: Pagination = new Pagination();
  isLoadingAlerts: WritableSignal<boolean> = signal(false);
  isLastPage: WritableSignal<boolean> = signal(false);
  totalElements: WritableSignal<number> = signal(0);
  isAuthenticated: Signal<boolean>;
  alertsSummary: WritableSignal<AlertsSummary | null> = signal(null);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.breadcrumbs = signal(
          this.createBreadcrumbs(this.activatedRoute.root),
        );
      });

    this.utilService.refreshBreadcrumb
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.breadcrumbs = signal(
          this.createBreadcrumbs(this.activatedRoute.root),
        );
      });
    this.pagination.sort = "createdAt,DESC";

    this.isAuthenticated = this.userService.isAuthenticated;
    toObservable(this.isAuthenticated).subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.pagination.page = 0;
        this.isLastPage.set(false);
        this.alerts.set([]);
        this.getAlertsSummary();
      }
    });
  }

  onAlertDismissed(alertId: string): void {
    this.alerts.update((currentAlerts) => {
      const index = currentAlerts.findIndex((alert) => alert.id === alertId);
      currentAlerts[index].alertStatus = AlertStatus.DISMISSED;
      return currentAlerts;
    });
  }

  onLoadMoreClicked(): void {
    this.pagination.page++;
    this.loadAlerts();
  }

  getAlertsSummary(): void {
    this.alertService.getAlertsSummary().subscribe((res) => {
      this.alertsSummary.set(res);
    });
  }

  checkIfFirstLoad(): void {
    if (this.alerts().length === 0) {
      this.loadAlerts();
    }
  }

  loadAlerts(): void {
    if (this.isLoadingAlerts() || this.isLastPage()) {
      return;
    }
    this.isLoadingAlerts.set(true);
    this.alertService
      .getGlobalAlerts(this.pagination)
      .pipe(finalize(() => this.isLoadingAlerts.set(false)))
      .subscribe((response) => {
        this.alerts.update((a) => {
          return [...a, ...response.content];
        });
        this.isLastPage.set(response.last);
        this.totalElements.set(response.totalElements);
      });
  }

  private createBreadcrumbs(
    route: ActivatedRoute,
    url: string = "",
    breadcrumbs: MenuItem[] = [],
  ): MenuItem[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url
        .map((segment) => segment.path)
        .join("/");
      if (routeURL !== "") {
        url += `/${routeURL}`;
      }
      let label;
      if (child.snapshot.data["dynamic"]) {
        label = child.snapshot.data["breadcrumb"];
      } else {
        if (child.snapshot.data["breadcrumb"]) {
          label = this.tLoco.translate(
            "breadcrumb." + child.snapshot.data["breadcrumb"],
          );
        }
      }
      const icon = child.snapshot.data["bIcon"] ?? "";

      if (label && !child.snapshot.data["skipBreadcrumb"]) {
        breadcrumbs.push({ label, routerLink: url, icon });
      }
      return this.createBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }
}
